/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import * as Accordion from '@radix-ui/react-accordion'
import React, { useState } from 'react'
import { MdExpandMore } from 'react-icons/md'

/** A description of the available properties for this component. */
type ExpandableContentProps = {
  /** The clickable title used to expand the content box. */
  title: string

  /** The content to show when the component is expanded. */
  content: React.ReactNode

  /** Whether the content should be expanded by default. */
  defaultOpen?: boolean

  /** An optional HTML id to use on the container element. */
  containerId?: string

  /** An optional icon to display next to the title. */
  icon?: React.ReactNode
}

/**
 * An accordion component that displays a title and content that can be expanded and collapsed. The title is always
 * visible, and the content is hidden until the user clicks on the title to expand it. An optional icon can be displayed
 * next to the title.
 */
const ExpandableContent: React.FC<ExpandableContentProps> = ({
  title,
  content,
  defaultOpen = false,
  icon,
  containerId,
}) => {
  const [value, setValue] = useState(defaultOpen ? 'accordion-item' : undefined)

  return (
    <Accordion.Root
      id={containerId}
      collapsible
      type={'single'}
      className={
        'flex flex-col gap-6 border-grey-50/20 border rounded-2xl bg-grey-450 px-10 py-6 my-2 text-white not-prose'
      }
      value={value}
      onValueChange={(newValue) => setValue(newValue)}
      asChild
    >
      <Accordion.Item value={'accordion-item'}>
        <Accordion.Trigger asChild>
          <div
            className={
              'group flex flex-row items-center justify-between cursor-pointer data-[state=closed]:text-primary-500 data-[state=open]:text-primary-500'
            }
          >
            <div className={'flex flex-row'}>
              {icon}
              <h2 className={'font-medium p-0 m-0'}>{title}</h2>
            </div>
            <MdExpandMore className={'group-data-[state=open]:rotate-180'} />
          </div>
        </Accordion.Trigger>
        <Accordion.Content
          className={
            'data-[state=open]:animate-slide-down data-[state=closed]:animate-slide-up overflow-hidden text-base font-light text-black'
          }
        >
          {content}
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  )
}

export { ExpandableContent }
export type { ExpandableContentProps }
